import { useEffect, useRef } from "react";
import type { ComposerCommand } from "./slashCommands";

export interface ComposerCommandListProps {
  id: string;
  /** The commands that match what's typed. */
  matches: ComposerCommand[];
  /** What's typed after the `/`, for the empty note. */
  query: string;
  highlighted: number;
  onHighlight: (index: number) => void;
  onPick: (command: ComposerCommand) => void;
  /** The list's accessible name ("Commands"). */
  label: string;
  /** "No commands match /xyz". */
  noMatch: (query: string) => string;
}

export const commandOptionId = (listId: string, index: number) => `${listId}-option-${index}`;

/**
 * The composer's command list (Figma "Slash command list"): above the message box, about five rows then scrolling.
 * Focus stays in the message box, which points at the highlighted row (aria-activedescendant); a click picks a row
 * without taking focus. With nothing matching it shows a note, until Esc or the `/` is deleted.
 */
export function ComposerCommandList({
  id,
  matches,
  query,
  highlighted,
  onHighlight,
  onPick,
  label,
  noMatch,
}: Readonly<ComposerCommandListProps>) {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    listRef.current
      ?.querySelector(`#${CSS.escape(commandOptionId(id, highlighted))}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [id, highlighted]);

  return (
    <div
      className="absolute bottom-full left-0 z-20 mb-2 w-[400px] max-w-full overflow-hidden rounded-xl border border-border-default bg-surface-elevated shadow-md"
      // Keeps focus (and the caret) in the message box when a row is clicked.
      onMouseDown={(event) => event.preventDefault()}
    >
      {matches.length === 0 ? (
        <p role="status" className="px-5 py-4 [font:var(--type-body-sm)] text-content-tertiary">
          {noMatch(query)}
        </p>
      ) : (
        <div
          ref={listRef}
          id={id}
          role="listbox"
          aria-label={label}
          // A scrolling region must be keyboard-reachable (axe scrollable-region-focusable). The arrows already scroll
          // it from the message box, and focus can't actually land here: while the list is open Tab picks a command,
          // and the list goes away as soon as the message box loses focus.
          tabIndex={0}
          className="flex max-h-[336px] flex-col gap-1 overflow-y-auto p-2"
        >
          {matches.map((command, index) => (
            // Buttons, like MenuOption: clickable, and focusable content for the scrolling list, though the message box
            // keeps focus (tabIndex -1) and drives the list with the arrows.
            <button
              type="button"
              key={command.name}
              id={commandOptionId(id, index)}
              role="option"
              aria-selected={index === highlighted}
              tabIndex={-1}
              onMouseEnter={() => onHighlight(index)}
              onClick={() => onPick(command)}
              className={`flex w-full cursor-pointer flex-col rounded-lg px-3 py-2.5 text-left ${index === highlighted ? "bg-surface-base" : ""}`}
            >
              {/* No 12px monospace type style yet (code is 14px): matches Figma's 12/18 until there is one. */}
              <span className="font-mono text-xs leading-[18px] text-brand-primary-on-subtle dark:text-brand-primary-50">
                /{command.name}
              </span>
              {command.description && (
                <span className="truncate [font:var(--type-body-2xs)] text-content-tertiary">
                  {command.description}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
